import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import { useInternalManagers } from './hooks/useInternalManagers';
import { IoMdArrowRoundBack } from "react-icons/io";
import { IoDocumentText, IoList, IoGrid, IoAdd, IoSave, IoWarning } from "react-icons/io5";
import { FaFileExcel } from "react-icons/fa";
import TextEdit from "../../molecules/TextEdit";
import SelectTextField from "../../molecules/SelectTextField";
import { useSignal } from "@preact/signals-react";
import useFetch from "../../../hooks/useFetch";
import PageForm from "../../templates/PageForm";
import NewTooltip from "../../atoms/NewTooltip";
import Dialog from "../../organisms/Dialog";
import { exportToExcel } from "../../../services/ExcelExportService";
import CreateOperationModal from "./CreateOperationModal";
import OperationTableView from "./components/OperationTableView";
import OperationDetailView from "./components/OperationDetailView";
import {
    STRUCTURING_ROUTES,
    STRUCTURING_BREADCRUMBS,
    STRUCTURING_API,
    formatCurrency,
    formatDate,
    OperationStatus,
} from './structuringUtils';
import {
    Operation,
    buildFetchUrl,
    buildFetchHeaders,
    parseOperationData,
    reloadOperationData,
    handleDeleteDocument as deleteDocumentBackend,
    handleDeleteInstallment as deleteInstallmentBackend,
    saveDocuments,
} from './OperationPageViewBackend';
import { editedReceivablesStore } from './EditedReceivablesStore';
import type { Document as OperationDocument, Receivable } from './OperationPageViewBackend';

const OperationPageView: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const apiVersion = useSignal('');
    const viewMode = useSignal<'detail' | 'list'>('list');
    const showDeleteDialog = useSignal(false);
    const showSaveDialog = useSignal(false);
    const deleteAction = useSignal<'document' | 'installment' | null>(null);
    const selectedDocument = useSignal<string>('');
    const selectedInstallment = useSignal<string>('');
    const isDeleting = useSignal(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRemoveValidationDialog, setShowRemoveValidationDialog] = useState(false);
    const [removeValidationJustification, setRemoveValidationJustification] = useState('');
    const internalManagers = useInternalManagers();
    const operation = useSignal<Operation>({
        operation_code: "",
        total_documents: 0,
        total_receivables: 0,
        total_amount: 0,
        processing_date: "",
        s3_location: "",
        documents: [],
        notes: "",
        status: ""
    });

    const fetchHeaders = useMemo(() => buildFetchHeaders(), []);
    const { isLoading, data, error, isTokenError } = useFetch(buildFetchUrl(id), fetchHeaders);

    useEffect(() => {
        editedReceivablesStore.clear();
        return () => editedReceivablesStore.clear();
    }, [id]);

    console.log('useFetch state:', { isLoading, data, error, isTokenError, id, API_URL: buildFetchUrl(id) });

    if (isTokenError) {
        navigate("/denied");
    }

    if (error) {
        navigate("/network-error");
    }

    useEffect(() => {
        if (data) {
            console.log('Raw API data:', data);
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
            operation.value = parseOperationData(data);
            console.log('Operation state after update:', operation.value);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const handleReloadData = async () => {
        const newData = await reloadOperationData(id);
        if (newData) {
            operation.value = newData;
        }
    };

    const handleDeleteDocument = (documentId: string) => {
        deleteAction.value = 'document';
        selectedDocument.value = documentId;
        showDeleteDialog.value = true;
    };

    const handleDeleteInstallment = (documentId: string, installmentNumber: string) => {
        deleteAction.value = 'installment';
        selectedDocument.value = documentId;
        selectedInstallment.value = installmentNumber;
        showDeleteDialog.value = true;
    };

    const confirmDelete = async () => {
        console.log('confirmDelete called');
        console.log('operation.value:', operation.value);
        console.log('operation.value.operation_code:', operation.value.operation_code);
        console.log('deleteAction.value:', deleteAction.value);
        console.log('selectedDocument.value:', selectedDocument.value);
        console.log('selectedInstallment.value:', selectedInstallment.value);

        isDeleting.value = true;
        try {
            if (deleteAction.value === 'document') {
                const result = await deleteDocumentBackend(
                    operation.value.operation_code,
                    selectedDocument.value
                );

                if (result.success) {
                    if (result.operationDeleted) {
                        navigate(STRUCTURING_ROUTES.list);
                    } else {
                        await handleReloadData();
                    }
                }
            } else if (deleteAction.value === 'installment') {
                const result = await deleteInstallmentBackend(
                    operation.value.operation_code,
                    selectedDocument.value,
                    selectedInstallment.value
                );

                if (result.success) {
                    await handleReloadData();
                }
            }
        } finally {
            isDeleting.value = false;
            showDeleteDialog.value = false;
            deleteAction.value = null;
            selectedDocument.value = '';
            selectedInstallment.value = '';
        }
    };

    const exportToExcelHandler = () => {
        const exportData: any[] = [];

        operation.value.documents.forEach((document: any) => {
            document.receivables.forEach((receivable: any) => {
                exportData.push({
                    indPag: receivable.ind_pag || '',
                    natOp: receivable.nat_op || '',
                    vPag: receivable.v_pag || '',
                    operacao: operation.value.operation_code,
                    cedente_nome: document.assignor_name,
                    cedente_cnpj: document.assignor_cnpj,
                    nfe: document.document_id,
                    titulo: document.document_title,
                    cpf_cnpj: document.debtor_cpf_cnpj,
                    nome: document.debtor_name,
                    logradouro: document.address_street,
                    numero: document.address_number,
                    complemento: document.address_complement,
                    bairro: document.address_district,
                    cod_ibge: document.address_ibge_code,
                    municipio: document.address_city,
                    uf: document.address_state,
                    cep: document.address_zip_code,
                    pais: document.address_country,
                    telefone: document.address_phone,
                    email: document.debtor_email,
                    cfop: document.cfop,
                    data_emissao: formatDate(document.issue_date),
                    ndup: receivable.receivable_number,
                    vdup: receivable.amount,
                    dvenc: receivable.due_date ? receivable.due_date.split('T')[0].split('-').reverse().join('/') : '',
                    valor_corrigido: '',
                    vencimento_corrigido: '',
                    parcela_adquirida: ''
                });
            });
        });

        const exportHeaders = [
            { id: 'indPag', title: 'indPag', field: 'indPag', canSort: false },
            { id: 'natOp', title: 'natOp', field: 'natOp', canSort: false },
            { id: 'vPag', title: 'vPag', field: 'vPag', canSort: false },
            { id: 'operacao', title: 'Operação', field: 'operacao', canSort: false },
            { id: 'cedente_nome', title: 'Cedente Nome', field: 'cedente_nome', canSort: false },
            { id: 'cedente_cnpj', title: 'Cedente CNPJ', field: 'cedente_cnpj', canSort: false },
            { id: 'nfe', title: 'NFe', field: 'nfe', canSort: false },
            { id: 'titulo', title: 'Titulo', field: 'titulo', canSort: false },
            { id: 'cpf_cnpj', title: 'Sacado CNPJ/CPF', field: 'cpf_cnpj', canSort: false },
            { id: 'nome', title: 'Sacado Nome', field: 'nome', canSort: false },
            { id: 'logradouro', title: 'Logradouro', field: 'logradouro', canSort: false },
            { id: 'numero', title: 'Numero', field: 'numero', canSort: false },
            { id: 'complemento', title: 'Complemento', field: 'complemento', canSort: false },
            { id: 'bairro', title: 'bairro', field: 'bairro', canSort: false },
            { id: 'cod_ibge', title: 'CodIBGE', field: 'cod_ibge', canSort: false },
            { id: 'municipio', title: 'municipio', field: 'municipio', canSort: false },
            { id: 'uf', title: 'UF', field: 'uf', canSort: false },
            { id: 'cep', title: 'CEP', field: 'cep', canSort: false },
            { id: 'pais', title: 'Pais', field: 'pais', canSort: false },
            { id: 'telefone', title: 'Telefone', field: 'telefone', canSort: false },
            { id: 'email', title: 'Email', field: 'email', canSort: false },
            { id: 'cfop', title: 'CFOP', field: 'cfop', canSort: false },
            { id: 'data_emissao', title: 'Data Emissão', field: 'data_emissao', canSort: false },
            { id: 'ndup', title: 'nDup', field: 'ndup', canSort: false },
            { id: 'vdup', title: 'vDup', field: 'vdup', canSort: false },
            { id: 'dvenc', title: 'dVenc', field: 'dvenc', canSort: false },
            { id: 'valor_corrigido', title: 'Valor Corrigido', field: 'valor_corrigido', canSort: false },
            { id: 'vencimento_corrigido', title: 'vencimento corrigido', field: 'vencimento_corrigido', canSort: false },
            { id: 'parcela_adquirida', title: 'Parcela adquirida', field: 'parcela_adquirida', canSort: false }
        ];

        exportToExcel(exportHeaders, exportData, `operacao-${operation.value.operation_code}.csv`);
    };

    const handleCompleteOperation = async () => {
        await handleReloadData();
    };

    const isOperationClosed = operation.value.status === OperationStatus.CLOSED;
    console.log('Operation status:', operation.value.status, 'isOperationClosed:', isOperationClosed);

    const handleReceivableChange = (document: OperationDocument, receivable: Receivable) => {
        editedReceivablesStore.upsert(document, receivable);
    };

    const handleSaveChanges = () => {
        showSaveDialog.value = true;
    };

    const confirmSaveChanges = async () => {
        const payload = editedReceivablesStore.buildPayload();
        console.log('[SaveChanges] payload:', JSON.stringify(payload, null, 2));

        const result = await saveDocuments(payload);
        if (result.success) {
            toast.success(result.message);
            editedReceivablesStore.clear();
        } else {
            toast.error(result.message);
        }
        showSaveDialog.value = false;
    };

    const handleSendToValidation = async () => {
        const op = operation.value;
        if (!op.operation_type || !op.obligation_type || !op.trader?.trim() || !op.analysis_criteria) {
            toast.error('Preencha todos os campos da configuração do lote.');
            return;
        }
        if (op.analysis_criteria === 'PARCIAL' && (!op.analysis_receivables_count || op.analysis_receivables_count < 1)) {
            toast.error('Informe a quantidade de títulos para análise.');
            return;
        }
        try {
            const response = await fetch(`${STRUCTURING_API.url}/configure/validation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-API-Key': STRUCTURING_API.key },
                body: JSON.stringify({
                    credit_operations_id: op.credit_operations_id,
                    operation_type: op.operation_type,
                    obligation_type: op.obligation_type,
                    trader: op.trader.trim(),
                    validation_xml: op.validation_xml || false,
                    validation_receipt_stub: op.validation_receipt_stub || false,
                    validation_contact: op.validation_contact || false,
                    validation_sefaz: op.validation_sefaz || false,
                    validation_invoice_pdf: op.validation_invoice_pdf || false,
                    validation_accepted_bill: op.validation_accepted_bill || false,
                    validation_purchase_contract: op.validation_purchase_contract || false,
                    analysis_criteria: op.analysis_criteria,
                    analysis_receivables_count: op.analysis_criteria === 'PARCIAL' ? op.analysis_receivables_count : op.total_receivables,
                }),
            });
            if (response.ok) {
                const result = await response.json().catch(() => ({}));
                operation.value = { ...operation.value, sent_to_validation: result.sent_to_validation ?? true };
                toast.success('Enviado para validação com sucesso!');
            } else {
                const result = await response.json().catch(() => ({}));
                toast.error(result.message || 'Erro ao enviar para validação.');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        }
    };

    const handleRemoveValidation = async () => {
        if (!removeValidationJustification.trim()) {
            toast.error('Informe a justificativa.');
            return;
        }
        try {
            const response = await fetch(`${STRUCTURING_API.url}/configure/validation/remove`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-API-Key': STRUCTURING_API.key },
                body: JSON.stringify({
                    credit_operations_id: operation.value.credit_operations_id,
                    justification: removeValidationJustification.trim(),
                }),
            });
            if (response.ok) {
                toast.success('Removido da validação com sucesso!');
                operation.value = {
                    ...operation.value,
                    sent_to_validation: false,
                    operation_type: '',
                    obligation_type: '',
                    trader: '',
                    validation_xml: false,
                    validation_receipt_stub: false,
                    validation_contact: false,
                    validation_sefaz: false,
                    validation_invoice_pdf: false,
                    validation_accepted_bill: false,
                    validation_purchase_contract: false,
                    analysis_criteria: '',
                    analysis_receivables_count: 0,
                };
                setShowRemoveValidationDialog(false);
                setRemoveValidationJustification('');
            } else {
                const result = await response.json().catch(() => ({}));
                toast.error(result.message || 'Erro ao remover da validação.');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        }
    };

    return (
        <PageForm
            pageTitle={operation.value.operation_code}
            apiVersion={apiVersion.value}
            isLoading={isLoading}
            breadcrumb={[
                STRUCTURING_BREADCRUMBS.operations,
                STRUCTURING_BREADCRUMBS.operationDetail,
            ]}
            controlSlot={
                <div className="flex justify-end space-x-2">
                    <button
                        onClick={() => setShowCreateModal(true)}
                        disabled={isOperationClosed}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <IoAdd />
                        <span>Fechar Lote</span>
                    </button>

                    <button
                        className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded"
                        onClick={() => navigate(-1)}
                    >
                        <IoMdArrowRoundBack />
                    </button>

                    <NewTooltip content="Exportar para Excel" alignment="left">
                        <button
                            type="button"
                            className="text-white bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 font-medium rounded text-sm px-4 py-2 flex items-center"
                            onClick={exportToExcelHandler}
                        >
                            <FaFileExcel className="mr-2" />
                            Exportar
                        </button>
                    </NewTooltip>
                </div>
            }
        >
            <div className="container mx-auto p-4">
                {isOperationClosed && (
                    <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded dark:bg-green-900 dark:border-green-600 dark:text-green-300">
                        <strong>Operação Concluída:</strong> Esta operação foi fechada e não pode mais ser editada.
                    </div>
                )}

                {operation.value.processing_errors && operation.value.processing_errors.length > 0 && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded dark:bg-red-900/30 dark:border-red-600">
                        <div className="flex items-center gap-2 mb-2">
                            <IoWarning className="text-red-500 w-5 h-5 flex-shrink-0" />
                            <strong className="text-red-700 dark:text-red-300">Erros no processamento de arquivos XML ({operation.value.processing_errors.length})</strong>
                        </div>
                        <ul className="list-disc list-inside space-y-1 text-sm text-red-600 dark:text-red-400">
                            {operation.value.processing_errors.map((err, idx) => (
                                <li key={idx}>
                                    <span className="font-medium">{err.file_name}</span>: {err.error}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="grid grid-cols-12 gap-4 mb-6">
                    <div className="col-span-4">
                        <TextEdit label="Valor Total:" value={formatCurrency(operation.value.total_amount)} disabled />
                    </div>
                    <div className="col-span-4">
                        <TextEdit label="Total de Documentos:" value={operation.value.documents.length.toString()} disabled />
                    </div>
                    <div className="col-span-4">
                        <TextEdit label="Total de Recebíveis:" value={operation.value.total_receivables.toString()} disabled />
                    </div>
                </div>

                {operation.value.notes && (
                    <div className="mb-6">
                        <TextEdit label="Observações:" value={operation.value.notes} disabled />
                    </div>
                )}

                <div className="mb-6 bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Configuração do Lote para Validação</h3>
                            {operation.value.sent_to_validation && (
                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                    ✓ Já enviado para validação
                                </span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={handleSendToValidation}
                                disabled={operation.value.sent_to_validation}
                                className="bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium px-4 py-2 rounded flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-500 dark:hover:bg-gray-600"
                            >
                                Enviar para Validação
                            </button>
                            {operation.value.sent_to_validation && (
                                <button
                                    type="button"
                                    onClick={() => setShowRemoveValidationDialog(true)}
                                    className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded dark:bg-red-500 dark:hover:bg-red-600"
                                >
                                    Remover da Validação
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mb-4">
                        <div className="col-span-3">
                            <SelectTextField
                                id="operation_type"
                                label="Tipo de Operação"
                                value={operation.value.operation_type || ''}
                                onChange={(e) => { operation.value = { ...operation.value, operation_type: e.target.value }; }}
                                disabled={operation.value.sent_to_validation}
                                items={[
                                    { value: 'CPRF', label: 'CPRF' },
                                    { value: 'CESSAO', label: 'Cessão' },
                                ]}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="obligation_type"
                                label="Obrigação"
                                value={operation.value.obligation_type || ''}
                                onChange={(e) => { operation.value = { ...operation.value, obligation_type: e.target.value }; }}
                                disabled={operation.value.sent_to_validation}
                                items={[
                                    { value: 'SEM_OBRIGACAO', label: 'Sem Obrigação' },
                                    { value: 'COM_REGRESSO', label: 'Com Regresso' },
                                    { value: 'SEM_REGRESSO', label: 'Sem Regresso' },
                                ]}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="trader"
                                label="Trader"
                                value={operation.value.trader || ''}
                                onChange={(e) => { operation.value = { ...operation.value, trader: e.target.value }; }}
                                disabled={operation.value.sent_to_validation}
                                items={internalManagers}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="analysis_criteria"
                                label="Critério de Análise"
                                value={operation.value.analysis_criteria || ''}
                                onChange={(e) => { operation.value = { ...operation.value, analysis_criteria: e.target.value }; }}
                                disabled={operation.value.sent_to_validation}
                                items={[
                                    { value: 'TOTAL', label: 'Total' },
                                    { value: 'PARCIAL', label: 'Parcial' },
                                ]}
                            />
                        </div>
                        {operation.value.analysis_criteria === 'PARCIAL' && (
                            <div className="col-span-3">
                                <TextEdit
                                    label={`Qtd. Títulos (max: ${operation.value.total_receivables})`}
                                    value={(operation.value.analysis_receivables_count || 0).toString()}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value) || 0;
                                        operation.value = { ...operation.value, analysis_receivables_count: Math.min(Math.max(val, 0), operation.value.total_receivables) };
                                    }}
                                    disabled={operation.value.sent_to_validation}
                                />
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Validações</label>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { key: 'validation_xml', label: 'XML' },
                                { key: 'validation_receipt_stub', label: 'Canhoto' },
                                { key: 'validation_contact', label: 'Ligação/Email' },
                                { key: 'validation_sefaz', label: 'SEFAZ' },
                                { key: 'validation_invoice_pdf', label: 'NF PDF' },
                                { key: 'validation_accepted_bill', label: 'Duplicata c/ Aceite' },
                                { key: 'validation_purchase_contract', label: 'Contrato Compra/Venda' },
                            ].map(({ key, label }) => (
                                <label key={key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={(operation.value as any)[key] || false}
                                        onChange={(e) => {
                                            operation.value = { ...operation.value, [key]: e.target.checked };
                                        }}
                                        disabled={operation.value.sent_to_validation}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mb-6 bg-gray-50 dark:bg-slate-700 border border-blue-300 dark:border-blue-500 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                            <IoDocumentText className="mr-2" />
                            Documentos ({operation.value.documents.length})
                        </h3>

                        <div className="flex items-center space-x-2">
                            <div className="flex space-x-1 bg-gray-100 dark:bg-slate-600 rounded p-1">
                                <NewTooltip content="Visão em detalhe" alignment="left">
                                    <button
                                        type="button"
                                        className={`p-2 rounded ${viewMode.value === 'detail' ? 'bg-white shadow text-gray-900 dark:bg-slate-500 dark:text-gray-100' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                        onClick={() => viewMode.value = 'detail'}
                                    >
                                        <IoGrid className="w-4 h-4" />
                                    </button>
                                </NewTooltip>
                                <NewTooltip content="Visão em lista" alignment="left">
                                    <button
                                        type="button"
                                        className={`p-2 rounded ${viewMode.value === 'list' ? 'bg-white shadow text-gray-900 dark:bg-slate-500 dark:text-gray-100' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                        onClick={() => viewMode.value = 'list'}
                                    >
                                        <IoList className="w-4 h-4" />
                                    </button>
                                </NewTooltip>
                            </div>

                            <button
                                type="button"
                                onClick={handleSaveChanges}
                                disabled={isOperationClosed}
                                className="bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium rounded text-sm px-4 py-2 flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <IoSave className="w-4 h-4" />
                                <span>Salvar</span>
                            </button>
                        </div>
                    </div>

                    {viewMode.value === 'detail' ? (
                        <OperationDetailView
                            documents={operation.value.documents}
                            totalReceivables={operation.value.total_receivables}
                            isDeleting={isDeleting.value}
                            isOperationClosed={isOperationClosed}
                            onDeleteDocument={handleDeleteDocument}
                            onDeleteInstallment={handleDeleteInstallment}
                            onReceivableChange={handleReceivableChange}
                        />
                    ) : (
                        <OperationTableView
                            documents={operation.value.documents}
                            isDeleting={isDeleting.value}
                            isOperationClosed={isOperationClosed}
                            onDeleteInstallment={handleDeleteInstallment}
                            onReceivableChange={handleReceivableChange}
                        />
                    )}
                </div>
            </div>

            <Dialog
                id="delete-confirmation"
                show={showDeleteDialog.value}
                onClose={() => showDeleteDialog.value = false}
                header={deleteAction.value === 'document' ? 'Deletar Documento' : 'Deletar Parcela'}
                body={
                    <div className="p-6">
                        <p className="text-gray-700 dark:text-gray-300">
                            {deleteAction.value === 'document'
                                ? 'Tem certeza que deseja deletar este documento? Esta ação não pode ser desfeita. Se for o último documento da operação, a operação inteira será removida.'
                                : 'Tem certeza que deseja deletar esta parcela? Esta ação não pode ser desfeita e as parcelas restantes serão reorganizadas.'
                            }
                        </p>
                    </div>
                }
                footer={
                    <div className="flex space-x-2">
                        <button
                            type="button"
                            onClick={() => showDeleteDialog.value = false}
                            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                            disabled={isDeleting.value}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={confirmDelete}
                            className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
                            disabled={isDeleting.value}
                        >
                            {isDeleting.value ? 'Deletando...' : 'Deletar'}
                        </button>
                    </div>
                }
            />

            <Dialog
                id="save-confirmation"
                show={showSaveDialog.value}
                onClose={() => showSaveDialog.value = false}
                header="Salvar Alterações"
                body={
                    <div className="p-6">
                        <p className="text-gray-700 dark:text-gray-300">
                            Deseja salvar as alterações realizadas nos recebíveis?
                        </p>
                    </div>
                }
                footer={
                    <div className="flex space-x-2">
                        <button
                            type="button"
                            onClick={() => showSaveDialog.value = false}
                            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={confirmSaveChanges}
                            className="px-4 py-2 text-white bg-blue-500 hover:bg-blue-600 rounded dark:bg-blue-600 dark:hover:bg-blue-700"
                        >
                            Confirmar
                        </button>
                    </div>
                }
            />

            <Dialog
                id="remove-validation-confirmation"
                show={showRemoveValidationDialog}
                onClose={() => { setShowRemoveValidationDialog(false); setRemoveValidationJustification(''); }}
                header="Remover da Validação"
                body={
                    <div className="p-6 space-y-4">
                        <p className="text-gray-700 dark:text-gray-300">
                            Tem certeza que deseja remover esta operação da validação?
                        </p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Justificativa *</label>
                            <textarea
                                value={removeValidationJustification}
                                onChange={(e) => setRemoveValidationJustification(e.target.value)}
                                rows={3}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                placeholder="Informe o motivo da remoção..."
                            />
                        </div>
                    </div>
                }
                footer={
                    <div className="flex space-x-2">
                        <button
                            type="button"
                            onClick={() => { setShowRemoveValidationDialog(false); setRemoveValidationJustification(''); }}
                            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleRemoveValidation}
                            className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded"
                        >
                            Confirmar Remoção
                        </button>
                    </div>
                }
            />

            <CreateOperationModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onComplete={handleCompleteOperation}
                operationId={id}
                operationSummary={{
                    totalAmount: operation.value.total_amount,
                    totalDocuments: operation.value.documents.length,
                    totalReceivables: operation.value.total_receivables
                }}
            />
        </PageForm>
    );
};

export default OperationPageView;
